import {
  ref as rtdbRef,
  get as rtdbGet,
  onValue,
  update as rtdbUpdate,
} from 'firebase/database';
import { rtdb } from './firebase';

/**
 * Config for the RN home "identify with AI" CTA. Lives in the Realtime Database
 * at `configuration/aiImageRecognition` (all watchlock app-config is RTDB, not
 * Firestore); the RN app reads `introCtaBackgroundUrl` to render the card
 * background and falls back to a bundled image when it's missing/empty. This
 * service powers the admin form that sets/clears it.
 */
const AI_IMAGE_RECOGNITION_PATH = '/configuration/aiImageRecognition';

/** Default opacity for the intro-CTA background; preserves prior RN behaviour. */
export const INTRO_CTA_BACKGROUND_OPACITY_DEFAULT = 0.22;

/** Title/subtitle text colour mode over the intro-CTA background image. */
export type IntroCtaTextMode = 'auto' | 'light' | 'dark';

/** Allowed text-mode values; anything else falls back to the default. */
export const INTRO_CTA_TEXT_MODES: readonly IntroCtaTextMode[] = ['auto', 'light', 'dark'];

/** Default text mode; `auto` lets the RN app follow the user's theme. */
export const INTRO_CTA_TEXT_MODE_DEFAULT: IntroCtaTextMode = 'auto';

export interface AiImageRecognitionConfig {
  /** Public download URL of the intro-CTA background image; '' = use bundled fallback. */
  introCtaBackgroundUrl: string;
  /** 0 = invisible, 1 = full image; how washed-out the bg renders behind the CTA text. */
  introCtaBackgroundOpacity: number;
  /** Forces the overlay title/subtitle colour; 'auto' = follow theme. */
  introCtaTextMode: IntroCtaTextMode;
}

export const DEFAULT_AI_IMAGE_RECOGNITION_CONFIG: AiImageRecognitionConfig = {
  introCtaBackgroundUrl: '',
  introCtaBackgroundOpacity: INTRO_CTA_BACKGROUND_OPACITY_DEFAULT,
  introCtaTextMode: INTRO_CTA_TEXT_MODE_DEFAULT,
};

/**
 * Reads the config off the `aiImageRecognition` node. The URL is a bare string
 * key; opacity is a number in [0, 1]. Missing/invalid values fall back to the
 * defaults (so the RN app's prior behaviour is preserved when unset).
 */
function normalise(value: unknown): AiImageRecognitionConfig {
  const node = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const rawOpacity = node.introCtaBackgroundOpacity;
  const rawTextMode = node.introCtaTextMode;
  return {
    introCtaBackgroundUrl:
      typeof node.introCtaBackgroundUrl === 'string' ? node.introCtaBackgroundUrl : '',
    introCtaBackgroundOpacity:
      typeof rawOpacity === 'number' && rawOpacity >= 0 && rawOpacity <= 1
        ? rawOpacity
        : INTRO_CTA_BACKGROUND_OPACITY_DEFAULT,
    introCtaTextMode: INTRO_CTA_TEXT_MODES.includes(rawTextMode as IntroCtaTextMode)
      ? (rawTextMode as IntroCtaTextMode)
      : INTRO_CTA_TEXT_MODE_DEFAULT,
  };
}

export async function getAiImageRecognitionConfig(): Promise<AiImageRecognitionConfig> {
  const snapshot = await rtdbGet(rtdbRef(rtdb, AI_IMAGE_RECOGNITION_PATH));
  return snapshot.exists() ? normalise(snapshot.val()) : DEFAULT_AI_IMAGE_RECOGNITION_CONFIG;
}

export function subscribeToAiImageRecognitionConfig(
  callback: (config: AiImageRecognitionConfig) => void,
): () => void {
  return onValue(rtdbRef(rtdb, AI_IMAGE_RECOGNITION_PATH), (snapshot) => {
    callback(snapshot.exists() ? normalise(snapshot.val()) : DEFAULT_AI_IMAGE_RECOGNITION_CONFIG);
  });
}

/**
 * Writes the intro-CTA background URL to RTDB. Pass an empty string to clear it
 * (RN falls back to the bundled default). Uses `update` on the parent node so
 * only the keys we pass are touched (sibling config keys are preserved) and the
 * provenance fields land alongside the URL.
 */
export async function setIntroCtaBackgroundUrl(url: string, userId?: string): Promise<void> {
  await rtdbUpdate(rtdbRef(rtdb, AI_IMAGE_RECOGNITION_PATH), {
    introCtaBackgroundUrl: url,
    introCtaBackgroundUpdatedAt: Date.now(),
    ...(userId ? { introCtaBackgroundUpdatedBy: userId } : {}),
  });
}

/**
 * Writes the intro-CTA background opacity (clamped to [0, 1]) to RTDB. Uses
 * `update` on the parent node so sibling keys (the URL, provenance) are kept.
 */
export async function setIntroCtaBackgroundOpacity(opacity: number, userId?: string): Promise<void> {
  const clamped = Math.min(1, Math.max(0, opacity));
  await rtdbUpdate(rtdbRef(rtdb, AI_IMAGE_RECOGNITION_PATH), {
    introCtaBackgroundOpacity: clamped,
    introCtaBackgroundUpdatedAt: Date.now(),
    ...(userId ? { introCtaBackgroundUpdatedBy: userId } : {}),
  });
}

/**
 * Writes the intro-CTA overlay text colour mode to RTDB. Uses `update` on the
 * parent node so it sits alongside the URL + opacity and sibling keys survive.
 */
export async function setIntroCtaTextMode(mode: IntroCtaTextMode, userId?: string): Promise<void> {
  await rtdbUpdate(rtdbRef(rtdb, AI_IMAGE_RECOGNITION_PATH), {
    introCtaTextMode: mode,
    introCtaBackgroundUpdatedAt: Date.now(),
    ...(userId ? { introCtaBackgroundUpdatedBy: userId } : {}),
  });
}
